import React, { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import VideoRenderer from "../components/VideoRenderer";
import Preloader from "../components/Preloader";
import { MonoButton } from "../kits";
import CustomCourseRequestPr from "../components/CustomCourseRequestPr";
import SeoHelmet from "./SeoHelmet";
import { Helmet } from "react-helmet";
import { generateCourseSchema } from "./utils/seoSchema";
import { ICON_IMAGE_URLS } from "./utils/iconImageMap";
import ModalWrapper from "./utils/ModalWrapper";
import RoughBorder from "../kits/LayoutKit/RoughBorder";
import ICONS, { ICON_COLORS } from "../config/icon";
import UnderlineSketch from "../components/UnderlineSketch";
import PaperBG from "../kits/DesignKit/PaperBG";

// Sterne-Bewertung
function StarRating({ value = 0, max = 5 }) {
  const fullStars = Math.floor(value);
  const hasHalf = value - fullStars >= 0.5;
  const emptyStars = max - fullStars - (hasHalf ? 1 : 0);
  return (
    <span className="gap-0.5 mb-1 text-black text-base">
      {Array(fullStars).fill(0).map((_, i) => <span key={`full-${i}`}>★</span>)}
      {hasHalf && <span key="half" className="text-black">☆</span>}
      {Array(emptyStars).fill(0).map((_, i) => <span key={`empty-${i}`}>☆</span>)}
    </span>
  );
}
function stringToList(text) {
  if (!text) return [];
  return text.split(/\n|,/).map(s => s.trim()).filter(Boolean);
}

function Course() {
  const { slug } = useParams();
  const [showProjectRequest, setShowProjectRequest] = useState(false);
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("content");
  const [openProjectIndex, setOpenProjectIndex] = useState(0);
  const [projects, setProjects] = useState([]);

  const contentRef = useRef(null); // Oben im Component-Body
  const projectBlockRef = useRef(null);
  
  // REFS
  const videoRef = useRef(null);
  const bulletsRef = useRef(null);
  const tabsRef = useRef(null);
  const detailsRef = useRef(null);
  const projectRefs = useRef([]);

  const skillRefs = useRef([]);



  const extractYouTubeId = (url) => {
    const match = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/))([^?&]+)/);
    return match ? match[1] : "";
  };

  useEffect(() => {
    if (projects.length > 0) setOpenProjectIndex(0);
  }, [projects]);

  useEffect(() => {
    if (course) {
      document.title = `Lern Einfach. ${course.title}`;
      document.documentElement.lang = "de";
      document.documentElement.dir = "ltr";
      document.documentElement.classList.add("bg-gray-50", "text-gray-900");
    }
  }, [course]);

  useEffect(() => {
    if (!course?.id) return;
    fetch(`https://www.denisbitter.de/stage01/api/get_projects.php?course_id=${course.id}`)
      .then((res) => res.json())
      .then((data) => {
        if (data.success) setProjects(data.projects);
        else setProjects([]);
      })
      .catch(() => setProjects([]));
  }, [course]);

  useEffect(() => {
    if (!slug) {
      setError("Ungültige Kurs-URL.");
      setLoading(false);
      return;
    }
    fetch(`https://www.denisbitter.de/stage01/api/get_courses.php?slug=${slug}`)
      .then((response) => {
        if (!response.ok) throw new Error("Fehler beim Laden der Kursdaten.");
        return response.json();
      })
      .then((data) => {
        if (data.success && data.data) setCourse(data.data);
        else throw new Error("Ungültige Kursdaten.");
        setLoading(false);
      })
      .catch((error) => {
        setError(error.message);
        setLoading(false);
      });
  }, [slug]);

  if (loading) return <Preloader />;
  if (error) return <p className="px-6 py-8 font-mono text-xs text-red-400">error: {error}</p>;
  if (!course) return <p className="px-6 py-8 font-mono text-xs">Der Kurs wurde nicht gefunden.</p>;

  // SEO-Infos etc.


  
  const pageUrl = `https://denisbitter.de/course/${slug}`;
  const pageTitle = `${course.title} – BITTER. LernEinfach`;
  const pageDescription = Array.isArray(course.description)
    ? course.description.join(" ")
    : course.description;
  const pageImage = course.image
    ? `https://denisbitter.de${course.image}`
    : "https://denisbitter.de/images/courses-og.jpg";
  const Icon = ICONS[course.icon] || ICONS.default;
  const colorClass = ICON_COLORS[course.icon] || ICON_COLORS.default;

  const descriptionExtensionArr = Array.isArray(course?.descriptionExtension)
    ? course.descriptionExtension
    : stringToList(course?.descriptionExtension);

  // ---------------- Tabs Content -------------------
  const renderTabContent = () => {
    switch (activeTab) {
   case "content":
  return (
    <div
      ref={contentRef}
      className="mb-6 rounded-lg p-4 border border-[#AC8E66]/20 relative"
    >
      {Array.isArray(course.skills) && course.skills.length > 0 ? (
        <div className="space-y-4">
          <div className="w-full rounded-lg pt-2 pb-2 pl-2 pr-2 shadow-lg bg-[#f0ede1]">
            <p className="p-4 text-xs sm:text-sm text-center">
              Dein Kurs Inhalt <br />nicht starr, <br /> flexibel
            </p>
          </div>
          {course.skills.map((skill, index) => (
            <div
              key={index}
              ref={el => (skillRefs.current[index] = el)}
              className="border rounded-lg p-4 relative mb-4"
            >
              <div className="flex items-center space-x-4 bg-[#151515] rounded-lg pt-1 pb-1">
                <div className="text-brand font-semibold font-mono text-xl px-2">
                  {String(index + 1).padStart(2, "0")}
                </div>
                <div>
                  <h3 className="text-[#EFEBDC] font-mono text-sm">{skill.name}</h3>
                  <p className="text-[#EFEBDC] mt-4 font-mono text-xs">{skill.description}</p>
                </div>
              </div>
              {skill.skillExample && (
                <p className="text-[#151515] mt-4 font-mono text-xs px-2">
                  {skill.skillExample}
                </p>
              )}
              {skill.mediaType === "image" && skill.mediaUrl && (
                <img
                  src={skill.mediaUrl}
                  alt={skill.name}
                  className="mt-2 w-full max-h-64 object-contain border rounded-lg"
                />
              )}
              {skill.mediaType === "video" && skill.mediaUrl && (
                <video controls className="mt-2 rounded w-full max-h-64 border">
                  <source src={skill.mediaUrl} type="video/mp4" />
                  Dein Browser unterstützt dieses Video nicht.
                </video>
              )}
              {skill.mediaType === "youtube" && skill.mediaUrl && (
                <div className="mt-2 rounded overflow-hidden aspect-video border">
                  <iframe
                    src={`https://www.youtube.com/embed/${extractYouTubeId(skill.mediaUrl)}`}
                    title="YouTube Video"
                    allowFullScreen
                    className="w-full h-full  pt-1 pb-1 border border-gray-800"
                  />
                </div>
              )}
              {/* RoughBorder FÜR DIESEN SKILL */}
              <RoughBorder
                parentRef={() => skillRefs.current[index]}
                radius={13}
                padding={2}
                color="#AC8E66" 
                strokeWidth={0.3}
                roughness={1}
                bowing={2}
              />
            </div>
          ))}
        </div>

         

      ) : (
   
    
        <p className="text-gray-600 font-mono text-xs px-5 py-5 text-center">
          Dieser Kurs ist ein individueller Projekt-Kurs. <br />
          Der Inhalt wird auf deine Projekt-Herausforderung abgestimmt.
        </p>
      )}
      <div className="mt-6">
      
        <p className="text-xs font-mono mb-4 mt-4 text-gray-600 px-5 text-center">
          Solltest du den Kurs bereits gebucht haben, findest du in Deinem Dashboard
          alle Inhalte und Projektinformationen für diesen Kurs.
        </p>
        
      </div>
      <Link
        to="/login"
        className="group inline-flex w-full items-center justify-center gap-2 font-mono border border-[#AC8E66]/30 rounded-md bg-[#151515] py-2 px-4 hover:border-[#AC8E66]/90 hover:bg-[#1c1c1c] transition-all duration-300 text-[#EFEBDC] text-xs sm:text-sm md:text-sm"
      >
        <span className="text-[#AC8E66]/40 transition-colors group-hover:text-[#AC8E66]/70">[</span>
        <span>Login <strong className="text-brand">+</strong> Dein Kurs Dashboard</span>
        <span className="inline-block transition-all duration-300 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 text-[#AC8E66]">→</span>
        <span className="text-[#AC8E66]/40 transition-colors group-hover:text-[#AC8E66]/70">]</span>
      </Link>
        <RoughBorder
        parentRef={contentRef}
        radius={10}
        padding={3}
      color="#AC8E66" 
        strokeWidth={0.3}
        roughness={1}
        bowing={2}
      />
    </div>

  );

  case "projects":
  return projects.length > 0 ? (
    <div
      ref={projectBlockRef}
      className="relative  
      bg-gradient-to-br from-gray-50 via-gray-50/25 to-orange-50
      rounded-lg shadow p-6 group mb-4"
    >
      <div className="text-xs border rounded-sm 
        bg-[#151515] text-slate-50
        pt-3 pb-3 text-center mb-6">
        Echte Projekte mit Teilnehmern
      </div>
      {projects.map((project, idx) => (
        <details
          key={project.id}
          ref={el => (projectRefs.current[idx] = el)}
          className="relative bg-gray-50 rounded-lg shadow p-4 group mb-4"
          open={openProjectIndex === idx}
          onClick={() => {
            setActiveTab("projects");
            setOpenProjectIndex(idx === openProjectIndex ? null : idx);
          }}
        >
          <summary className="cursor-pointer font-mono rounded-lg text-sm pt-2 pb-2 text-shadow flex items-start">
            <span className="text-xs font-semibold py-3">{project.title}</span>
          </summary>
          <div className="mt-2 text-sm text-[#151515] space-y-2 font-mono px-0">
            {/* Medienanzeige */}
            {project.mediaType === "image" && project.mediaUrl && (
              <img
                src={project.mediaUrl}
                alt={project.title}
                className="mt-4 rounded w-full max-h-64 object-contain"
              />
            )}
            {/* Problem */}
            <p className="text-xs font-mono">
              <span className="block bg-[#151515] text-brand px-4 py-1 text-left">Problem:</span>
              <span className="block pt-4 pl-4">{project.problem}</span>
               <br />
            </p>
          
            {/* Lösung */}
            <p className="text-xs font-mono">
              <span className="block bg-[#151515] text-slate-50 px-4 py-1 text-left">Lösung:</span>
              <span className="block pt-4 pl-4">{project.solution}</span>
            </p>
           
            {/* Ergebnis */}
            {project.impact && (
              <p className="text-xs font-mono">
                <span className="block bg-[#151515] text-slate-50 px-4 py-1 text-left">Ergebnis:</span>
                <span className="block pt-4 pl-4">{project.impact}</span>
 <br />
              </p>
            )}
            {/* Training */}
            <p className="text-xs font-mono">
              <span className="block bg-[#151515] text-slate-50 px-4 py-1 text-left">Training:</span>
              {project.durationDays !== null && (
                <span className="text-xs pt-4  pl-4 block"><strong>Dauer:</strong> {project.durationDays} Tage</span>
              )}
              <span className="block pt-4 pl-4">{project.training}</span>
        <br />
            </p>
            {/* Projekttyp */}
            {project.projectType && (
              <p className="text-xs font-mono">
                <span className="block bg-[#151515] text-slate-50 px-4 py-1 text-left">Projekttyp:</span>
                <span className="block pt-4 pl-4">{project.projectType}</span>
    <br />
              </p>
            )}
            {project.mediaType === "video" && project.mediaUrl && (
              <video controls className="mt-4 w-full rounded-lg border border-gray-800">
                <source src={project.mediaUrl} type="video/mp4" />
                Dein Browser unterstützt dieses Video nicht.
              </video>
            )}
            {project.mediaType === "youtube" && project.mediaUrl && (
              <div className="mt-4 aspect-video ">
                <iframe
                  src={`https://www.youtube.com/embed/${extractYouTubeId(project.mediaUrl)}`}
                  title="YouTube Video"
                  allowFullScreen
                  className="w-full h-full rounded-lg border border-gray-800 pt-2 pb-2 pl-2 pr-2 bg-gray-50 shadow-lg "
                ></iframe>
              </div>
            )}
          </div>

          {/* Einzelne RoughBorder für jedes Accordion */}
          <RoughBorder
            parentRef={() => projectRefs.current[idx]}
            radius={10}
            padding={2}
          color="#AC8E66" 
            strokeWidth={0.3}
            roughness={1}
            bowing={1}
          />
        </details>
      ))}
      {/* Border um den gesamten Projektblock */}
      <RoughBorder
        parentRef={projectBlockRef}
        radius={10}
        padding={3}
      color="#AC8E66" 
        strokeWidth={0.3}
        roughness={1}
        bowing={2}
      />
    </div>
  ) : (
    // Fallback, wenn keine Projekte
    <div
      ref={projectBlockRef}
      className="relative w-full h-full rounded-lg pt-2 pb-2 pl-2 pr-2 shadow-lg"
    >
      <p className="text-xs sm:text-sm text-center">
        Für diesen Kurs wurden noch keine Projekt-Trainings freigeschaltet.
      </p>
      <div>
        <MonoButton
          className="w-full text-xs mt-5 hover:text-brand"
          onClick={() => setShowProjectRequest(true)}
        >
          Projekt <span className="text-brand hover:text-[#D9D4C5]">+</span> Training anfragen
        </MonoButton>
      </div>
      <RoughBorder
        parentRef={projectBlockRef}
        radius={10}
        padding={3}
      color="#AC8E66" 
        strokeWidth={0.7}
        roughness={0.3}
        bowing={0.3}
      />
    </div>
  );



      

     



      default:
        return null;
    }
  };

  const courseSchema = generateCourseSchema({
    ...course,
    image: ICON_IMAGE_URLS[course.icon] || "https://www.denisbitter.de/images/icons/default-min.png"
  });

  return (
    <>
      {showProjectRequest && (
        <ModalWrapper onClose={() => setShowProjectRequest(false)}>
          <CustomCourseRequestPr navbarHeight={0} />
        </ModalWrapper>
      )}
      {course && (
        <SeoHelmet
          title={`Lern Einfach. ${course.title} `}
          description={pageDescription}
          url={pageUrl}
          image={pageImage}
        />
      )}
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(courseSchema)}
        </script>
      </Helmet>
      <PaperBG>
        {/* Kopfzeile, Teaser */}
        <section className="container mx-auto max-w-2xl
         px-6 py-8">
          <div className="flex flex-col md:flex-row md:justify-between md:items-center">
            <div>
              <div className="max-w-4xl mx-auto px-0 py-2 
              bg-[#171717] rounded-lg border border-[#D9D4C5]/80">
                <div className="px-4">
                  <nav className="text-xs text-brand mb-0 font-mono">
                    <Link to="/courses" className="text-[#EFEBDC] hover:text-brand">Alle Kurse</Link>
                    {" / "}
                    <span className="text-brand">{course.title}</span>
                  </nav>
                </div>
              </div>
              <div className="flex items-start p-4">
                <div className="ml-4">
                  <h1 className="text-xl md:text-2xl font-semibold leading-snug">{course.title}</h1>
                  {Array.isArray(course.description) ? (
                    <div className="sm:ml-0">
                      <ul className="list-disc pl-4 text-[#151515] mt-4 
                      font-mono text-xs leading-relaxed space-y-1">
                        {course.description.map((line, idx) =>
                          line?.trim() && (
                            <li key={idx}>{line}</li>
                          )
                        )}
                      </ul>
                    </div>
                  ) : (
                    <p className="max-w-prose text-[#151515] mt-4 font-mono text-xs leading-relaxed">
                      {course.description}
                    </p>
                  )}
                  <div className="mt-8 sm:ml-0 text-xs text-[#151515]">
                    <p>Ein Kurs von <span className="text-[#AC8E66]">{course.instructor}</span></p>
                  </div>
                  <div className="text-xs text-[#151515]">
                    <span className="sm:ml-0">{course.registeredCount || 0} Lernende Menschen</span>
                    <span> {course.ratingValue || 0} von 5  <StarRating value={course.ratingValue || 0} max={5} /></span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        {/* Haupt-Content: Video | Bulletpoints | Tabs */}
        <section className="container mx-auto max-w-6xl px-6 py-8 grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
          {/* Linker Content-Bereich */}
          <div className="md:col-span-2 flex flex-col gap-6">
            {/* VIDEO */}
            <div ref={videoRef} className="relative bg-[#d9d4c5] rounded-xl p-2 mb-6">
              {course.videoUrl ? (
                <VideoRenderer url={course.videoUrl} />
              ) : (
                <div className="bg-[#d9d4c5] h-64 flex items-center justify-center text-brand">
                  Kein Video verfügbar
                </div>
              )}
              <RoughBorder 
              parentRef={videoRef} 
              radius={10} 
              padding={2} 
            color="#AC8E66"  
              strokeWidth={0.3} 
              roughness={1} 
              bowing={2} />
            </div>
            {/* BULLETS */}
            <div ref={bulletsRef} className="relative  rounded-lg p-6 text-xs mb-6">
              {descriptionExtensionArr.length > 0 ? (
                <ul className="list-disc ml-6 space-y-1">
                  {descriptionExtensionArr.map((line, idx) => line?.trim() && <li key={idx}>{line}</li>)}
                </ul>
              ) : course?.descriptionExtension ? (
                <span>{course.descriptionExtension}</span>
              ) : null}
              <RoughBorder parentRef={bulletsRef} radius={10} padding={2} color="#AC8E66"  strokeWidth={0.3} roughness={1} bowing={2} />
            </div>
            {/* TABS */}
            <div ref={tabsRef} className="relative flex items-center justify-center
             rounded-lg p-4 mb-6 border border-[#AC8E66]/20">
              <div className="flex flex-wrap  items-center justify-center gap-4">
                {["content", "projects"].map((key) => {
                  const isActive = activeTab === key;
                  const label = key === "content" 
                  ? "Inhalt" 
                  : "Echte Projekt Beispiele";
                  return (
                    <MonoButton
                      key={key}
                      onClick={() => setActiveTab(key)}
                      className={`px-4 py-2 rounded-md font-mono text-xs transition-all cursor-pointer border ${
                        isActive
                          ? "text-[#EFEBDC] bg-[#171717] hover:text-[#AC8E66]"
                          : "text-[#EFEBDC]/60 bg-[#151515] border-[#AC8E66]/20 hover:text-[#AC8E66]"
                      }`}
                    >
                      {label}
                    </MonoButton>
                  );
                })}
              </div>
              <RoughBorder 
              parentRef={tabsRef} 
              radius={10} 
              padding={2} 
            color="#AC8E66"  
              strokeWidth={0.3} 
              roughness={1} 
              bowing={2} 
              />
            </div>
            {/* TAB INHALT */}
            <div className="relative">{renderTabContent()}</div>
          </div>
          {/* RECHTE DETAILSBOX */}
          <aside
            className="md:col-span-1 self-start md:sticky h-fit"
            style={{ top: "calc(var(--navbar-height, 0px) + 12px)" }}
          >
          <div
            ref={detailsRef}
            className="p-6 rounded-lg shadow-sm animate-border-2 h-max bg-[#f0ede1]"
          >
            <div>
              <div className="flex items-start">
                {Icon && (
                  <Icon className={`w-6 h-6 mt-0 shrink-0 ${colorClass}`} />
                )}
                <h2 className="ml-3 text-sm sm:text-xl md:text-xl leading-snug">
                  {course.title}
                </h2>
              </div>
              <UnderlineSketch />
              <div className="flex justify-between text-xs font-mono sm:text-sm md:text-sm">
                <span className="text-left w-1/2">Vor Ort:</span>
                <span className="text-right w-1/2">
                  {course.price && course.price !== "0"
                    ? `${course.price} €`
                    : "Preis auf Anfrage"}
                </span>
              </div>
              <div className="flex justify-between text-xs font-mono sm:text-sm md:text-sm">
                <span className="text-left w-1/2">Online:</span>
                <span className="text-right w-1/2">
                  {course.priceOnline && course.priceOnline !== "0"
                    ? `${course.priceOnline} €`
                    : "Preis auf Anfrage"}
                </span>
              </div>
              <div className="flex justify-between text-xs font-mono sm:text-sm md:text-sm">
                <span className="text-left w-1/2">Dauer:</span>
                <span className="text-right w-1/2">
                  {course.durationDays ? `${course.durationDays} Tage` : "individuell"}
                </span>
              </div>
              <UnderlineSketch />
            </div>
            <ul className="mt-4 space-y-2 text-[#151515] text-xs font-mono">
              <li className="flex items-start">
                <span className="mr-2 text-[#AC8E66]">✓</span>
                <span>Start, wann es für dich passt – kein künstlicher Takt</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2 text-[#AC8E66]">✓</span>
                <span>Keine Voraussetzungen – nur Neugier</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2 text-[#AC8E66]">✓</span>
                <span>Dein Tempo, dein Alltag – keine Hektik, kein Druck</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2 text-[#AC8E66]">✓</span>
                <span>Praxisnahe Projekte – echt, nicht konstruiert</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2 text-[#AC8E66]">✓</span>
                <span>Persönliche Begleitung – kein Bot, kein Callcenter</span>
              </li>
            </ul>
            <Link
              to={`/anmeldung/${slug}`}
              state={{ course }}
              className="group inline-flex w-full mt-8 items-center justify-center gap-2 font-mono border border-[#AC8E66]/30 rounded-md bg-[#151515] py-2 px-4 hover:border-[#AC8E66]/90 hover:bg-[#1c1c1c] transition-all duration-300 text-[#EFEBDC] text-sm"
            >
              <span className="text-[#AC8E66]/40 transition-colors group-hover:text-[#AC8E66]/70">[</span>
              <span>Unverbindlich anmelden</span>
              <span className="inline-block transition-all duration-300 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 text-[#AC8E66]">→</span>
              <span className="text-[#AC8E66]/40 transition-colors group-hover:text-[#AC8E66]/70">]</span>
            </Link>
            <RoughBorder parentRef={detailsRef} radius={10} padding={2} color="#AC8E66"  strokeWidth={0.3} roughness={1} bowing={2} />
          </div>
          </aside>
        </section>
      </PaperBG>
    </>
  );
}

export default Course;
